/**
 * Forward utm_* attribution params onto links into the app.
 *
 * Visitors land on langwatch.ai with utm params, but signup happens on
 * app.langwatch.ai: a plain <a href> hop drops the query string, so the
 * campaign that earned the visit never reaches the app's analytics. This
 * module rewrites app links just in time so the params survive the hop.
 *
 * How: `initUtmForwarding()` (called once from `entry-client.tsx`, never
 * during the SSR prerender) remembers the landing page's utm_* params in
 * sessionStorage, then installs capture-phase listeners that rewrite an
 * anchor's href at the moment it is about to be used. The browser reads the
 * href only after event dispatch finishes, so rewriting in the capture phase
 * covers plain clicks, cmd/ctrl click, middle click (auxclick),
 * target="_blank", and the right-click context menu (whose "Open in new tab"
 * and "Copy link address" read the href after the menu opens).
 *
 * Consent-independent by design: this rewrites link targets only. It sets no
 * cookies and sends no events, so it runs for every visitor, unlike
 * analytics.ts which only boots after the visitor accepts cookies. The
 * sessionStorage entry is first-party, per-tab, and holds nothing but the
 * utm params the visitor arrived with.
 */

export const UTM_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const

export type UtmParams = Record<string, string>

export const APP_HOSTNAME = 'app.langwatch.ai'

export const UTM_STORAGE_KEY = 'lw-utm-params'

/** The utm_* params present in a query string (`?a=b` and `a=b` both work). */
export function extractUtmParams(search: string): UtmParams {
  const params = new URLSearchParams(search)
  const utms: UtmParams = {}
  for (const key of UTM_PARAM_KEYS) {
    const value = params.get(key)
    if (value) utms[key] = value
  }
  return utms
}

/**
 * Merge utm params into a link to the app. Returns the href untouched when it
 * does not point at app.langwatch.ai, when nothing is missing, or when it is
 * not a parseable URL. Params already on the href always win: only utm_* keys
 * the href lacks are appended, so a hand-tuned campaign link keeps its values.
 */
export function mergeUtmsIntoAppHref(
  href: string,
  utms: UtmParams,
  baseHref = 'https://langwatch.ai/',
): string {
  let url: URL
  try {
    url = new URL(href, baseHref)
  } catch {
    return href
  }
  if (url.hostname !== APP_HOSTNAME) return href
  let changed = false
  for (const key of UTM_PARAM_KEYS) {
    const value = utms[key]
    if (value && !url.searchParams.has(key)) {
      url.searchParams.set(key, value)
      changed = true
    }
  }
  return changed ? url.href : href
}

/** sessionStorage when it is reachable, else null (SSR, private mode, blocked). */
export function defaultSessionStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null
    return window.sessionStorage
  } catch {
    return null
  }
}

/** The utm params remembered from the landing page, or {} when none. */
export function readStoredUtms(storage: Storage | null = defaultSessionStorage()): UtmParams {
  try {
    const raw = storage?.getItem(UTM_STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return {}
    const utms: UtmParams = {}
    for (const key of UTM_PARAM_KEYS) {
      const value = (parsed as Record<string, unknown>)[key]
      if (typeof value === 'string' && value) utms[key] = value
    }
    return utms
  } catch {
    return {}
  }
}

/** Remember the landing page's utm params for later app links in this tab. */
export function storeUtms(utms: UtmParams, storage: Storage | null = defaultSessionStorage()): void {
  try {
    storage?.setItem(UTM_STORAGE_KEY, JSON.stringify(utms))
  } catch {
    // Storage unavailable: links on this very page still get the params
    // straight from the URL; only later internal navigation loses them.
  }
}

let installed = false

/**
 * Idempotent client-only entry point. Remembers the landing utm params so they
 * survive internal navigation (client-side navigation pushes bare pathnames,
 * and full page loads drop the query string too), then installs the
 * just-in-time href rewriting for every link to the app, sitewide: nav,
 * landing CTAs, footer, and anything added later.
 */
export function initUtmForwarding(): void {
  if (installed) return
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  installed = true

  // A landing with utm params replaces the stored set: the latest campaign
  // that brought the visitor here wins the attribution.
  const landing = extractUtmParams(window.location.search)
  if (Object.keys(landing).length > 0) storeUtms(landing)

  document.addEventListener('click', rewriteAppAnchor, true)
  document.addEventListener('auxclick', rewriteAppAnchor, true)
  document.addEventListener('contextmenu', rewriteAppAnchor, true)
}

/**
 * Capture-phase handler: when the interaction happens on (or inside) an
 * anchor pointing at the app, merge the visitor's utm params into its href.
 * Idempotent, and it never prevents default or stops propagation, so the
 * cta_click tracking in analytics.ts and the browser's own navigation are
 * untouched (they just see the rewritten href).
 */
function rewriteAppAnchor(event: Event): void {
  const target = event.target
  if (!(target instanceof Element)) return
  const anchor = target.closest('a[href]')
  if (!(anchor instanceof HTMLAnchorElement)) return
  const href = anchor.getAttribute('href')
  if (!href) return

  // Params on the current URL win over the remembered landing set, though in
  // practice they match: a utm landing overwrites the stored set on init.
  const utms = { ...readStoredUtms(), ...extractUtmParams(window.location.search) }
  if (Object.keys(utms).length === 0) return

  const merged = mergeUtmsIntoAppHref(href, utms, window.location.href)
  if (merged !== href) anchor.setAttribute('href', merged)
}
